import { useState } from 'react';
import { useParams } from 'react-router-dom';

import styled from 'styled-components';

import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { useExternalConfirmSupplyOrderMutation } from 'data/__generated__';

const EMAIL_HEADER = 'https://totems3.s3.eu-central-1.amazonaws.com/assets/header-black.png';
const EMAIL_FOOTER = 'https://totems3.s3.eu-central-1.amazonaws.com/assets/yellow-footer.png';

const NETWORK_FAILURE_MESSAGE =
    "La confirmation n'a pas abouti. Réessayez dans quelques instants, ou confirmez la commande en répondant au mail reçu.";

/**
 * Les scanners de liens des messageries (Microsoft Safe Links) ouvrent la page avant le fournisseur : une
 * confirmation au chargement validait la commande à sa place. Elle attend donc un clic.
 */
export const SupplyOrderConfirmation = (): JSX.Element => {
    const { token = '' } = useParams<{ token: string }>();
    const [finalMessage, setFinalMessage] = useState<string | null>(null);
    const [failureMessage, setFailureMessage] = useState<string | null>(null);

    const [confirmSupplyOrder, { loading }] = useExternalConfirmSupplyOrderMutation();

    const handleConfirm = async (): Promise<void> => {
        setFailureMessage(null);

        try {
            const { data } = await confirmSupplyOrder({ variables: { token } });
            const result = data?.externalConfirmSupplyOrder;

            if (result?.success) {
                setFinalMessage('La commande a bien été validée. ✅');
                return;
            }

            // Lien invalide ou commande déjà confirmée : un nouveau clic ne changerait rien.
            setFinalMessage(result?.error || NETWORK_FAILURE_MESSAGE);
        } catch {
            setFailureMessage(NETWORK_FAILURE_MESSAGE);
        }
    };

    return (
        <Container>
            <ImageContainer $height={250}>
                <Image src={EMAIL_HEADER} alt="Totem Header" />
            </ImageContainer>
            <TextContainer>
                {finalMessage ? (
                    <Label data-test="supply-order-confirmation-message">{finalMessage}</Label>
                ) : (
                    <>
                        {failureMessage ? <Label>{failureMessage}</Label> : null}
                        <TotemPrimaryLoadingButton
                            onClick={handleConfirm}
                            loading={loading}
                            label="Confirmer la commande"
                            useIcon={false}
                            $minWidth="280px"
                            data-test="supply-order-confirm-button"
                        />
                    </>
                )}
            </TextContainer>
            <ImageContainer $height={150}>
                <Image src={EMAIL_FOOTER} alt="Totem Footer" />
            </ImageContainer>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: hidden;
`;

const ImageContainer = styled.div<{ $height: number }>`
    width: 100%;
    height: ${({ $height }) => `${$height}px`};
`;

const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex-grow: 1;
    justify-content: center;
    align-items: center;
    padding: 0 16px;
`;

const Label = styled.div`
    font-size: 22px;
    text-align: center;
`;
